import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { sportsOutcomeOf } from "@townsy/contracts";
import type { VenueHudProps } from "../types";
import { OUTCOME_BANNER_MS, outcomeBanner, type OutcomeBannerView } from "./outcome";

/**
 * The outcome of your own shot, kick, point or match in big letters over the court for a moment ("Shot made! +2",
 * "Missed shot", "Goal!", "Point lost"). Mounted by both the full overlay and the compact strip, so it shows either
 * way. The server decided the outcome; this only displays the latest one.
 */
export function OutcomeBanner({ events, localActorId }: Pick<VenueHudProps, "events" | "localActorId">) {
  const latest = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!;
      const o = sportsOutcomeOf(e.data);
      const view = o ? outcomeBanner(o, localActorId) : null;
      if (view) return { id: e.id, at: e.at, view };
    }
    return null;
  }, [events, localActorId]);
  const [shown, setShown] = useState<{ id: number; view: OutcomeBannerView } | null>(null);

  useEffect(() => {
    if (!latest) return;
    const left = OUTCOME_BANNER_MS - (Date.now() - latest.at);
    if (left <= 0) return;
    setShown({ id: latest.id, view: latest.view });
    const t = window.setTimeout(() => setShown((s) => (s?.id === latest.id ? null : s)), left);
    return () => window.clearTimeout(t);
  }, [latest]);

  if (!shown || typeof document === "undefined") return null;
  return createPortal(
    <div key={shown.id} className={`sports-outcome ${shown.view.tone}`} role="status" aria-live="assertive" data-testid="sports-outcome" data-tone={shown.view.tone}>
      <strong className="sports-outcome-title">{shown.view.title}</strong>
      {shown.view.detail ? <span className="sports-outcome-detail">{shown.view.detail}</span> : null}
    </div>,
    document.body,
  );
}
